import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4navdqjh8g73q739r80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#0A1628]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/fce97f28-2f4c-44f9-98f0-5699095037af.png" alt="BOBST FIRENZE SRL" className="h-12 w-auto" />
              <div className="flex flex-col">
                <span className="text-lg font-bold whitespace-nowrap">BOBST FIRENZE SRL</span>
                <div className="h-0.5 w-16 bg-[#00D9FF]"></div>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-10">
              <button onClick={() => scrollToSection('home')} className="text-white hover:text-[#00D9FF] transition-colors relative group whitespace-nowrap cursor-pointer">
                Home
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('about')} className="text-white hover:text-[#00D9FF] transition-colors relative group whitespace-nowrap cursor-pointer">
                About
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('vpn')} className="text-white hover:text-[#00D9FF] transition-colors relative group whitespace-nowrap cursor-pointer">
                VPN
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('security')} className="text-white hover:text-[#00D9FF] transition-colors relative group whitespace-nowrap cursor-pointer">
                Security
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-white hover:text-[#00D9FF] transition-colors relative group whitespace-nowrap cursor-pointer">
                Contact
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('legal')} className="text-white hover:text-[#00D9FF] transition-colors relative group whitespace-nowrap cursor-pointer">
                Legal
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#00D9FF] group-hover:w-full transition-all duration-300"></span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-white cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-4">
              <button onClick={() => scrollToSection('home')} className="block text-white hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">Home</button>
              <button onClick={() => scrollToSection('about')} className="block text-white hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">About</button>
              <button onClick={() => scrollToSection('vpn')} className="block text-white hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">VPN</button>
              <button onClick={() => scrollToSection('security')} className="block text-white hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">Security</button>
              <button onClick={() => scrollToSection('contact')} className="block text-white hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">Contact</button>
              <button onClick={() => scrollToSection('legal')} className="block text-white hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">Legal</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center relative overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F1F3D] to-[#1A1F3A]"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#00D9FF] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#00D9FF] rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-20 relative z-10 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                Enterprise-Grade VPN<br />
                <span className="text-[#00D9FF]">Security Solutions</span>
              </h1>
              <p className="text-xl md:text-2xl text-[#B8C5D6] mb-10 leading-relaxed">
                Protect your digital infrastructure with military-grade encryption and zero-log architecture
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <button onClick={() => scrollToSection('contact')} className="px-8 py-4 bg-[#00D9FF] text-[#0A1628] rounded-full font-semibold hover:bg-[#00F0FF] transition-all duration-300 hover:shadow-lg hover:shadow-[#00D9FF]/50 hover:scale-105 whitespace-nowrap cursor-pointer">
                  Get Started
                </button>
                <button onClick={() => scrollToSection('vpn')} className="text-[#00D9FF] hover:text-[#00F0FF] transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  Learn More <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 md:h-[500px] relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/9c432f55e40427d48175f21458bb0655.jpg" 
                  alt="VPN Security" 
                  className="w-full h-full object-contain object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-[#F5F7FA] text-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12">
            <div className="md:col-span-2">
              <p className="text-[#00D9FF] text-xs font-semibold tracking-widest uppercase mb-4">ABOUT US</p>
              <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                Securing Digital Communications Since Foundation
              </h2>
            </div>
            <div className="md:col-span-3 space-y-6">
              <p className="text-lg text-[#4A5568] leading-relaxed">
                BOBST FIRENZE SRL is a leading provider of enterprise-grade VPN security solutions, dedicated to protecting businesses and individuals in an increasingly connected world. Our mission is to deliver uncompromising security without sacrificing performance or usability.
              </p>
              <p className="text-lg text-[#4A5568] leading-relaxed">
                Based in Italy, we combine cutting-edge encryption technology with a commitment to user privacy. Our zero-log policy ensures that your online activities remain completely private, while our global server network provides fast, reliable connections wherever you are.
              </p>
              <p className="text-lg text-[#4A5568] leading-relaxed">
                We believe that digital security is a fundamental right. That's why we've built our VPN solution from the ground up with security-first principles, implementing military-grade encryption standards and advanced protocols to keep your data safe from prying eyes.
              </p>

              <div className="grid sm:grid-cols-3 gap-4 mt-8">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-map-pin-line text-2xl text-[#00D9FF]"></i>
                  </div>
                  <p className="text-sm font-semibold text-[#8892A6] mb-2">ADDRESS</p>
                  <p className="text-sm font-mono text-[#0A1628]">VIA FRATELLI CERVI 76</p>
                  <p className="text-sm font-mono text-[#0A1628]">CAMPI BISENZIO (CAPALLE)</p>
                  <p className="text-sm font-mono text-[#0A1628]">50013, Italy</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-mail-line text-2xl text-[#00D9FF]"></i>
                  </div>
                  <p className="text-sm font-semibold text-[#8892A6] mb-2">EMAIL</p>
                  <a href="mailto:contact@bobstfirenzesrl.com" className="text-sm font-mono text-[#0A1628] hover:text-[#00D9FF] transition-colors cursor-pointer break-all">
                    contact@bobstfirenzesrl.com
                  </a>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-phone-line text-2xl text-[#00D9FF]"></i>
                  </div>
                  <p className="text-sm font-semibold text-[#8892A6] mb-2">PHONE</p>
                  <a href="tel:+16593004607" className="text-sm font-mono text-[#0A1628] hover:text-[#00D9FF] transition-colors cursor-pointer">
                    +1 659 300 4607
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Features Section */}
      <section id="vpn" className="py-24 bg-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, #00D9FF 35px, #00D9FF 37px)',
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-4">Advanced VPN Capabilities</h2>
            <p className="text-xl text-[#B8C5D6]">Enterprise-level features for complete digital protection</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-lock-line',
                title: 'Military-Grade Encryption',
                description: 'AES-256 encryption standard ensures your data remains completely secure and unreadable to unauthorized parties, providing bank-level security for all your online activities.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Zero-Log Policy',
                description: 'We never track, collect, or store your online activities. Your privacy is absolute with our strict no-logs policy, independently audited and verified.'
              },
              {
                icon: 'ri-global-line',
                title: 'Global Server Network',
                description: 'Access high-speed servers strategically located worldwide, ensuring optimal performance and reliability no matter where you connect from.'
              },
              {
                icon: 'ri-shield-flash-line',
                title: 'Kill Switch Protection',
                description: 'Automatic kill switch instantly disconnects your internet if VPN connection drops, preventing any data leaks and maintaining your privacy at all times.'
              },
              {
                icon: 'ri-device-line',
                title: 'Multi-Platform Support',
                description: 'Seamlessly protect all your devices with native apps for Windows, macOS, iOS, Android, and Linux. One subscription covers unlimited devices.'
              },
              {
                icon: 'ri-time-line',
                title: '24/7 Secure Connection',
                description: 'Always-on protection with automatic reconnection ensures your connection remains secure around the clock, with 99.9% uptime guarantee.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-[#1A2332] p-8 rounded-2xl hover:border-2 hover:border-[#00D9FF] transition-all duration-300 group hover:shadow-xl hover:shadow-[#00D9FF]/20 cursor-pointer">
                <div className="w-20 h-20 bg-gradient-to-br from-[#00D9FF] to-[#0099CC] rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${feature.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-[#B8C5D6] leading-relaxed">{feature.description}</p>
                <div className="mt-6 w-4/5 h-0.5 bg-[#00D9FF] mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Technology Section */}
      <section id="security" className="py-24 bg-white text-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#00D9FF] text-xs font-semibold tracking-widest uppercase mb-4">SECURITY FIRST</p>
            <h2 className="text-4xl md:text-5xl font-bold">Bank-Level Encryption Standards</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-16 items-center mt-16">
            <div className="relative">
              <div className="w-full h-96 md:h-[500px] relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Isometric%203D%20illustration%20of%20encryption%20process%20with%20animated%20data%20flow%20showing%20secure%20VPN%20tunnel%20with%20glowing%20cyan%20particles%20binary%20code%20streams%20and%20digital%20locks%20representing%20AES-256%20encryption%20in%20clean%20white%20background%20with%20technical%20diagrams%20and%20security%20protocols%20visualization&width=600&height=600&seq=security-encryption-tech&orientation=squarish" 
                  alt="Encryption Technology" 
                  className="w-full h-full object-contain object-center"
                />
              </div>
            </div>

            <div className="space-y-12">
              {[
                {
                  icon: 'ri-key-2-line',
                  title: 'AES-256 Encryption',
                  description: 'The same encryption standard used by governments and financial institutions worldwide. Your data is protected by virtually unbreakable 256-bit keys, ensuring maximum security against any threats.'
                },
                {
                  icon: 'ri-shield-keyhole-line',
                  title: 'OpenVPN & IKEv2 Protocols',
                  description: 'Industry-leading VPN protocols offering the perfect balance of security and speed. Choose between OpenVPN for maximum security or IKEv2 for optimal mobile performance.'
                },
                {
                  icon: 'ri-radar-line',
                  title: 'DNS Leak Protection',
                  description: 'Advanced DNS leak protection ensures your DNS queries are always encrypted and routed through our secure servers, preventing any exposure of your browsing activities.'
                },
                {
                  icon: 'ri-lock-unlock-line',
                  title: 'Perfect Forward Secrecy',
                  description: 'Each VPN session uses unique encryption keys that are never reused. Even if a key is compromised, your past and future sessions remain completely secure and protected.'
                }
              ].map((item, index) => (
                <div key={index} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#00D9FF] rounded-full flex items-center justify-center">
                      <i className={`${item.icon} text-2xl text-white`}></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-[#4A5568] leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(#00D9FF 1px, transparent 1px), linear-gradient(90deg, #00D9FF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h2>
            <p className="text-xl text-[#B8C5D6]">We're here to answer your security questions</p>
          </div>

          <div className="bg-[#1A2332] p-8 md:p-12 rounded-2xl shadow-2xl">
            <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name"
                  required
                  className="w-full px-4 py-4 bg-[#0A1628] border-2 border-transparent focus:border-[#00D9FF] rounded-lg text-white placeholder-[#8892A6] transition-colors outline-none" 
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email"
                  required
                  className="w-full px-4 py-4 bg-[#0A1628] border-2 border-transparent focus:border-[#00D9FF] rounded-lg text-white placeholder-[#8892A6] transition-colors outline-none" 
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium mb-2">Subject</label>
                <input 
                  type="text" 
                  id="subject" 
                  name="subject"
                  required
                  className="w-full px-4 py-4 bg-[#0A1628] border-2 border-transparent focus:border-[#00D9FF] rounded-lg text-white placeholder-[#8892A6] transition-colors outline-none" 
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-2">Message</label>
                <textarea 
                  id="message" 
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-4 bg-[#0A1628] border-2 border-transparent focus:border-[#00D9FF] rounded-lg text-white placeholder-[#8892A6] transition-colors outline-none resize-none" 
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
                <p className="text-xs text-[#8892A6] mt-2">Maximum 500 characters</p>
              </div>

              {formStatus === 'success' && (
                <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg">
                  Thank you! Your message has been sent successfully.
                </div>
              )}

              {formStatus === 'error' && (
                <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg">
                  Something went wrong. Please try again.
                </div>
              )}

              <button 
                type="submit" 
                disabled={formStatus === 'submitting'}
                className="w-full py-4 bg-[#00D9FF] text-[#0A1628] rounded-lg font-semibold hover:bg-[#00F0FF] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
            </form>

            <div className="grid md:grid-cols-3 gap-6 mt-12 pt-12 border-t border-[#8892A6]/20">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-2xl text-[#00D9FF]"></i>
                </div>
                <p className="text-sm text-[#8892A6] mb-1">Email</p>
                <a href="mailto:contact@bobstfirenzesrl.com" className="text-sm text-white hover:text-[#00D9FF] transition-colors cursor-pointer break-all">
                  contact@bobstfirenzesrl.com
                </a>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-2xl text-[#00D9FF]"></i>
                </div>
                <p className="text-sm text-[#8892A6] mb-1">Phone</p>
                <a href="tel:+16593004607" className="text-sm text-white hover:text-[#00D9FF] transition-colors cursor-pointer">
                  +1 659 300 4607
                </a>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-line text-2xl text-[#00D9FF]"></i>
                </div>
                <p className="text-sm text-[#8892A6] mb-1">Address</p>
                <p className="text-sm text-white">VIA FRATELLI CERVI 76<br />CAMPI BISENZIO, Italy</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Section */}
      <section id="legal" className="py-24 bg-[#F5F7FA] text-[#0A1628]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Legal Information</h2>
            <p className="text-xl text-[#4A5568]">Transparency and compliance at the core of our operations</p>
          </div>

          <div className="space-y-12">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <i className="ri-file-text-line text-[#00D9FF]"></i>
                Privacy Policy
              </h3>
              <p className="text-[#4A5568] leading-relaxed mb-4">
                At BOBST FIRENZE SRL, we are committed to protecting your privacy. Our zero-log policy means we do not collect, store, or share any information about your online activities. We only collect minimal data necessary for account management and service provision.
              </p>
              <p className="text-[#4A5568] leading-relaxed">
                Your connection logs, browsing history, traffic data, and DNS queries are never recorded. We use industry-standard security measures to protect any account information you provide, and we will never sell or share your data with third parties.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <i className="ri-article-line text-[#00D9FF]"></i>
                Terms of Service
              </h3>
              <p className="text-[#4A5568] leading-relaxed mb-4">
                By using BOBST FIRENZE SRL VPN services, you agree to use our service in compliance with all applicable laws and regulations. Our service is provided for legitimate privacy and security purposes only.
              </p>
              <p className="text-[#4A5568] leading-relaxed mb-4">
                Users are prohibited from using our service for any illegal activities, including but not limited to: unauthorized access to computer systems, distribution of malware, copyright infringement, or any activities that violate local, national, or international laws.
              </p>
              <p className="text-[#4A5568] leading-relaxed">
                We reserve the right to terminate service to users who violate these terms. All subscriptions are subject to our refund policy as outlined in your account agreement.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <i className="ri-cookie-line text-[#00D9FF]"></i>
                Cookie Policy
              </h3>
              <p className="text-[#4A5568] leading-relaxed mb-4">
                Our website uses minimal cookies necessary for basic functionality and security. We use session cookies to maintain your login state and preference cookies to remember your settings.
              </p>
              <p className="text-[#4A5568] leading-relaxed">
                We do not use tracking cookies or third-party advertising cookies. You can control cookie settings through your browser preferences. Disabling cookies may affect some website functionality but will not impact VPN service performance.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <i className="ri-building-line text-[#00D9FF]"></i>
                Company Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6 text-[#4A5568]">
                <div>
                  <p className="font-semibold text-[#0A1628] mb-2">Registered Company Name</p>
                  <p>BOBST FIRENZE SRL</p>
                </div>
                <div>
                  <p className="font-semibold text-[#0A1628] mb-2">Registration Country</p>
                  <p>Italy</p>
                </div>
                <div>
                  <p className="font-semibold text-[#0A1628] mb-2">Registered Address</p>
                  <p>VIA FRATELLI CERVI 76<br />CAMPI BISENZIO (CAPALLE)<br />50013, Italy</p>
                </div>
                <div>
                  <p className="font-semibold text-[#0A1628] mb-2">Contact Information</p>
                  <p>Email: contact@bobstfirenzesrl.com<br />Phone: +1 659 300 4607</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/fce97f28-2f4c-44f9-98f0-5699095037af.png" alt="BOBST FIRENZE SRL" className="h-10 w-auto" />
                <span className="text-lg font-bold whitespace-nowrap">BOBST FIRENZE SRL</span>
              </div>
              <p className="text-sm text-[#8892A6] mb-6">Enterprise VPN Security Solutions</p>
              <p className="text-xs text-[#8892A6]">Protecting your digital privacy with military-grade encryption and zero-log commitment.</p>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-5">Quick Links</h4>
              <div className="space-y-4">
                <button onClick={() => scrollToSection('home')} className="block text-sm text-[#8892A6] hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">Home</button>
                <button onClick={() => scrollToSection('about')} className="block text-sm text-[#8892A6] hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">About</button>
                <button onClick={() => scrollToSection('vpn')} className="block text-sm text-[#8892A6] hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">VPN Features</button>
                <button onClick={() => scrollToSection('security')} className="block text-sm text-[#8892A6] hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">Security</button>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-5">Legal</h4>
              <div className="space-y-4">
                <button onClick={() => scrollToSection('legal')} className="block text-sm text-[#8892A6] hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">Privacy Policy</button>
                <button onClick={() => scrollToSection('legal')} className="block text-sm text-[#8892A6] hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">Terms of Service</button>
                <button onClick={() => scrollToSection('legal')} className="block text-sm text-[#8892A6] hover:text-[#00D9FF] transition-colors whitespace-nowrap cursor-pointer">Cookie Policy</button>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-5">Contact</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-[#00D9FF] text-base mt-1"></i>
                  <p className="text-sm text-[#8892A6]">VIA FRATELLI CERVI 76<br />CAMPI BISENZIO (CAPALLE)<br />50013, Italy</p>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-[#00D9FF] text-base"></i>
                  <a href="mailto:contact@bobstfirenzesrl.com" className="text-sm text-[#8892A6] hover:text-[#00D9FF] transition-colors cursor-pointer break-all">contact@bobstfirenzesrl.com</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-[#00D9FF] text-base"></i>
                  <a href="tel:+16593004607" className="text-sm text-[#8892A6] hover:text-[#00D9FF] transition-colors cursor-pointer">+1 659 300 4607</a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#8892A6]/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-xs text-[#8892A6]">© 2025 BOBST FIRENZE SRL. All rights reserved.</p>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-[#8892A6] hover:text-[#00D9FF] transition-colors cursor-pointer whitespace-nowrap">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
